import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShoppingCart, Trash2, WandSparkles } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { useCart } from "../../../context/CartContext";
import { AiChat } from "../../../components/common/AiChat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";

// Free text ("20 κιβ. χαρτί κουζίνας, 5 απορρυπαντικό") → matched product lines → basket
export const QuickOrder = () => {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const { addMany } = useCart();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const parse = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/parse-order", { text }); setRes(data); } catch { toast.error(t("ai.error")); } finally { setBusy(false); }
  };
  const setQty = (i, v) => setRes((r) => ({ ...r, lines: r.lines.map((l, k) => (k === i ? { ...l, qty_boxes: Math.max(0, Number(v) || 0) } : l)) }));
  const add = () => {
    const rows = res.lines.filter((l) => l.qty_boxes > 0);
    addMany(rows.map((l) => ({ item_code: l.item_code, qty_boxes: l.qty_boxes, uom: l.uom })));
    toast.success(t("ai.addedToCart", { n: rows.length }));
    nav("/app/cart");
  };
  return (
    <div className="space-y-4" data-testid="quick-order">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={t("ai.quickPh")} className="w-full rounded-2xl bg-white p-4 text-base text-navy shadow-sm outline-none focus:ring-4 focus:ring-brand/20" data-testid="quick-order-text" />
      <button type="button" disabled={busy || text.trim().length < 2} onClick={parse} className="flex h-12 items-center gap-2 rounded-full bg-navy px-5 text-sm font-semibold text-white disabled:opacity-50" data-testid="quick-order-parse">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <WandSparkles className="h-4 w-4" />} {t("ai.quickParse")}
      </button>
      {res && (
        <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="quick-order-result">
          {res.lines.length === 0 && <p className="text-sm text-slate-500">{t("ai.noMatch")}</p>}
          <ul className="divide-y divide-slate-100">
            {res.lines.map((l, i) => (
              <li key={l.item_code} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`quick-line-${l.item_code}`}>
                <div className="min-w-0 flex-1"><p className="truncate font-semibold text-navy">{l[`name_${lang}`]}</p><p className="truncate text-xs text-slate-500">{l.item_code} · {l.units_per_box} {t("x.uom.piece")}/{t("x.uom.box")}{l.why ? ` · ${l.why}` : ""}</p></div>
                <input type="number" min={0} value={l.qty_boxes} onChange={(e) => setQty(i, e.target.value)} className="h-10 w-16 rounded-lg border border-slate-200 text-center font-bold text-navy" data-testid={`quick-qty-${l.item_code}`} />
                <span className="w-10 text-xs text-slate-600">{t(l.uom === "piece" ? "x.uom.piece" : "x.uom.box")}</span>
                <button type="button" onClick={() => setQty(i, 0)} className="grid h-9 w-9 place-items-center rounded-full text-rose-600 hover:bg-rose-50" aria-label={t("shop.removeFromCart")}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
          {res.unmatched?.length > 0 && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="quick-unmatched">{t("ai.unmatched")}: {res.unmatched.join(" · ")}</p>}
          {res.lines.some((l) => l.qty_boxes > 0) && <button type="button" onClick={add} className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-navy sm:w-auto sm:px-6" data-testid="quick-order-add"><ShoppingCart className="h-4 w-4" /> {t("ai.addToCart")}</button>}
        </div>
      )}
    </div>
  );
};

export default function Assistant() {
  const { t } = useI18n();
  return (
    <div className="space-y-4" data-testid="assistant-page">
      <h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("ai.title")}</h1>
      <Tabs defaultValue="ask">
        <TabsList className="h-12 rounded-full bg-white p-1 shadow-sm">
          <TabsTrigger value="ask" className="h-10 rounded-full px-4 data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="assistant-tab-ask">{t("ai.tabAsk")}</TabsTrigger>
          <TabsTrigger value="quick" className="h-10 rounded-full px-4 data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="assistant-tab-quick">{t("ai.tabQuick")}</TabsTrigger>
        </TabsList>
        <TabsContent value="ask" className="mt-4"><AiChat path="/ai/ask" sessionKey="customer" suggestions={[t("ai.s1"), t("ai.s2"), t("ai.s3")]} testId="ai-customer" /></TabsContent>
        <TabsContent value="quick" className="mt-4"><QuickOrder /></TabsContent>
      </Tabs>
    </div>
  );
}
